import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import OrganizationCombobox from '../components/OrganizationCombobox';
import { EngagementCreatedWork } from '../components/EngagementCreatedWork';
import { EngagementSessions } from '../components/EngagementSessions';
import { toast } from 'sonner';
import { formatDateTime, formatCurrency, STAGE_DISPLAY_NAMES } from '../lib/utils';
import {
  ArrowLeft, Briefcase, Users, Building2, MessageSquare, Loader2, Plus, X,
  Search, UserPlus, Pencil, Link2,
} from 'lucide-react';

const STATUSES = ['Planned', 'Active', 'On Hold', 'Completed', 'Archived'];
const STATUS_COLORS = {
  Planned: 'bg-blue-100 text-blue-700', Active: 'bg-emerald-100 text-emerald-700',
  'On Hold': 'bg-amber-100 text-amber-700', Completed: 'bg-gray-100 text-gray-700', Archived: 'bg-gray-100 text-gray-500',
};

export default function EngagementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, put, post, del } = useApi();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Add-person flow
  const [addChoiceOpen, setAddChoiceOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState([]);
  // Link-opportunity flow
  const [linkOppOpen, setLinkOppOpen] = useState(false);
  const [oppResults, setOppResults] = useState([]);
  const [orgEditing, setOrgEditing] = useState(false);
  const [workCount, setWorkCount] = useState(null);
  const [sessionCount, setSessionCount] = useState(null);

  const canEdit = user?.role !== 'executive_viewer';

  const fetchData = useCallback(async () => {
    try {
      setData(await get(`/engagements/${id}`));
    } catch (e) {
      toast.error('Engagement not found');
      navigate('/engagements');
    } finally {
      setLoading(false);
    }
  }, [get, id, navigate]);

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContacts = useCallback(async (q) => {
    try {
      const res = await get('/contacts', q ? { search: q } : undefined);
      const linked = new Set((data?.participants || []).map((p) => p.contact_id));
      setContactResults((res || []).filter((c) => !linked.has(c.id)));
    } catch (e) { /* noop */ }
  }, [get, data]);

  useEffect(() => {
    if (!addExistingOpen) return;
    const t = setTimeout(() => loadContacts(contactQuery), 250);
    return () => clearTimeout(t);
  }, [contactQuery, addExistingOpen, loadContacts]);

  const openLinkOpp = async () => {
    setLinkOppOpen(true);
    try {
      const res = await get('/opportunities');
      const linked = new Set((data?.opportunities || []).map((o) => o.id));
      setOppResults((res || []).filter((o) => !linked.has(o.id)));
    } catch (e) { /* noop */ }
  };

  if (loading || !data) {
    return <Layout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;
  }

  const { engagement: eng, organization, participants, opportunities, timeline } = data;

  const openEdit = () => {
    setForm({ name: eng.name || '', type: eng.type || '', status: eng.status || 'Planned', description: eng.description || '', start_date: eng.start_date || '', end_date: eng.end_date || '' });
    setEditOpen(true);
  };
  const saveEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await put(`/engagements/${id}`, form); toast.success('Engagement updated'); setEditOpen(false); fetchData(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  const quickStatus = async (status) => {
    try { await put(`/engagements/${id}`, { status }); toast.success(`Status: ${status}`); fetchData(); }
    catch (err) { toast.error(err.message); }
  };
  const addParticipant = async (contactId) => {
    try { await post(`/engagements/${id}/contacts`, { contact_id: contactId }); toast.success('Person added'); setAddExistingOpen(false); fetchData(); }
    catch (err) { toast.error(err.message); }
  };
  const removeParticipant = async (contactId) => {
    if (!window.confirm('Remove this person from the engagement? The contact is kept.')) return;
    try { await del(`/engagements/${id}/contacts/${contactId}`); toast.success('Person removed'); fetchData(); }
    catch (err) { toast.error(err.message); }
  };
  const updateRole = async (contactId, role) => {
    try { await put(`/engagements/${id}/contacts/${contactId}`, { role }); fetchData(); }
    catch (err) { toast.error(err.message); }
  };
  const setOrg = async (orgId) => {
    try {
      if (orgId) await put(`/engagements/${id}/organization`, { organization_id: orgId });
      else await del(`/engagements/${id}/organization`);
      toast.success('Organization updated'); setOrgEditing(false); fetchData();
    } catch (err) { toast.error(err.message); }
  };
  const linkOpp = async (oppId) => {
    try { await post(`/engagements/${id}/opportunities`, { opportunity_id: oppId }); toast.success('Opportunity linked'); setLinkOppOpen(false); fetchData(); }
    catch (err) { toast.error(err.message); }
  };
  const unlinkOpp = async (oppId) => {
    try { await del(`/engagements/${id}/opportunities/${oppId}`); toast.success('Opportunity unlinked'); fetchData(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="engagement-detail-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-sm"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl flex items-center gap-2"><Briefcase className="h-6 w-6" />{eng.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={`rounded-sm text-xs ${STATUS_COLORS[eng.status] || ''}`}>{eng.status}</Badge>
                {eng.type && <Badge variant="outline" className="rounded-sm text-xs">{eng.type}</Badge>}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Select value={eng.status} onValueChange={quickStatus}>
                <SelectTrigger className="w-[150px] rounded-sm" data-testid="engagement-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" className="rounded-sm" onClick={openEdit} data-testid="edit-engagement-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
            </div>
          )}
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="flex w-full justify-start gap-1 overflow-x-auto rounded-sm sm:grid sm:grid-cols-7 [-webkit-overflow-scrolling:touch]">
            <TabsTrigger value="overview" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="people" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-people-tab">People ({participants.length})</TabsTrigger>
            <TabsTrigger value="organization" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-org-tab">Organization</TabsTrigger>
            <TabsTrigger value="opportunities" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-opps-tab">Opportunities ({opportunities.length})</TabsTrigger>
            <TabsTrigger value="sessions" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-sessions-tab">Sessions{sessionCount !== null ? ` (${sessionCount})` : ''}</TabsTrigger>
            <TabsTrigger value="work" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-work-tab">Created Work{workCount !== null ? ` (${workCount})` : ''}</TabsTrigger>
            <TabsTrigger value="timeline" className="flex-shrink-0 whitespace-nowrap rounded-sm sm:flex-1" data-testid="eng-timeline-tab">Timeline</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4">
            <Card className="rounded-sm border"><CardContent className="p-5 space-y-3 text-sm">
              <div><span className="text-muted-foreground">Status: </span>{eng.status}</div>
              <div><span className="text-muted-foreground">Type: </span>{eng.type || '—'}</div>
              <div><span className="text-muted-foreground">Dates: </span>{eng.start_date || '—'} → {eng.end_date || '—'}</div>
              <div><span className="text-muted-foreground">Description: </span>{eng.description || '—'}</div>
              <div><span className="text-muted-foreground">Owner: </span>{eng.owner_name || '—'}</div>
            </CardContent></Card>
          </TabsContent>

          {/* People */}
          <TabsContent value="people" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">People ({participants.length})</CardTitle>
                {canEdit && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setAddChoiceOpen(true)} data-testid="eng-add-person-btn"><Plus className="h-4 w-4 mr-1.5" />Add Person</Button>}
              </CardHeader>
              <CardContent>
                {participants.length === 0 ? (
                  <div className="text-center py-8"><Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No people linked</p></div>
                ) : (
                  <div className="space-y-2">
                    {participants.map((p) => (
                      <div key={p.contact_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm gap-3" data-testid={`eng-person-row-${p.contact_id}`}>
                        <Link to={`/contacts/${p.contact_id}`} className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><span className="text-xs font-medium text-primary">{(p.contact_name || ' ')[0]}</span></div>
                          <div className="min-w-0"><p className="font-medium truncate">{p.contact_name}</p><p className="text-xs text-muted-foreground truncate">{p.contact_email}</p></div>
                        </Link>
                        <Input placeholder="Role (optional)" defaultValue={p.role || ''} onBlur={(e) => { if ((e.target.value || '') !== (p.role || '')) updateRole(p.contact_id, e.target.value); }} className="w-40 h-8 rounded-sm text-xs" disabled={!canEdit} data-testid={`eng-person-role-${p.contact_id}`} />
                        {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive flex-shrink-0" onClick={() => removeParticipant(p.contact_id)} data-testid={`eng-person-remove-${p.contact_id}`}><X className="h-4 w-4" /></Button>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization */}
          <TabsContent value="organization" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4"><CardTitle className="text-base font-medium">Organization</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {orgEditing ? (
                  <div className="space-y-2">
                    <OrganizationCombobox value={eng.organization_id || ''} valueName={organization?.name || ''} onChange={(oid) => setOrg(oid)} />
                    <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setOrgEditing(false)}>Cancel</Button>
                  </div>
                ) : organization ? (
                  <div className="flex items-center justify-between">
                    <Link to={`/organizations/${organization.id}`} className="flex items-center gap-2 text-primary hover:underline"><Building2 className="h-4 w-4" />{organization.name}</Link>
                    {canEdit && <div className="flex gap-2"><Button size="sm" variant="outline" className="rounded-sm" onClick={() => setOrgEditing(true)} data-testid="eng-change-org-btn">Change</Button><Button size="sm" variant="ghost" className="rounded-sm text-destructive" onClick={() => setOrg('')} data-testid="eng-remove-org-btn">Remove</Button></div>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">No organization linked</p>
                    {canEdit && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setOrgEditing(true)} data-testid="eng-add-org-btn"><Plus className="h-4 w-4 mr-1.5" />Add Organization</Button>}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Opportunities */}
          <TabsContent value="opportunities" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">Opportunities ({opportunities.length})</CardTitle>
                {canEdit && <Button size="sm" variant="outline" className="rounded-sm" onClick={openLinkOpp} data-testid="eng-link-opp-btn"><Link2 className="h-4 w-4 mr-1.5" />Link Existing</Button>}
              </CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <div className="text-center py-8"><Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No opportunities linked</p></div>
                ) : (
                  <div className="space-y-2">
                    {opportunities.map((o) => (
                      <div key={o.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm gap-3" data-testid={`eng-opp-row-${o.id}`}>
                        <Link to={`/opportunity/${o.id}`} className="min-w-0 flex-1">
                          <p className="font-medium truncate">{o.company_name}</p>
                          <div className="flex items-center gap-2 mt-1"><Badge variant="outline" className="rounded-sm text-xs">{STAGE_DISPLAY_NAMES[o.stage] || o.stage}</Badge><span className="text-xs text-muted-foreground">{formatCurrency(o.deal_value)}</span></div>
                        </Link>
                        {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive flex-shrink-0" onClick={() => unlinkOpp(o.id)} data-testid={`eng-opp-unlink-${o.id}`}><X className="h-4 w-4" /></Button>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Created Work */}
          <TabsContent value="sessions" className="mt-4 data-[state=inactive]:hidden" forceMount>
            <EngagementSessions engagementId={id} canEdit={canEdit} onCountChange={setSessionCount} />
          </TabsContent>

          <TabsContent value="work" className="mt-4 data-[state=inactive]:hidden" forceMount>
            <EngagementCreatedWork engagementId={id} organizationId={organization?.id} canEdit={canEdit} onCountChange={setWorkCount} />
          </TabsContent>

          {/* Timeline */}
          <TabsContent value="timeline" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4"><CardTitle className="text-base font-medium">Timeline ({timeline.length})</CardTitle></CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-8"><MessageSquare className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No activity yet</p></div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((a) => (
                      <div key={a.id} className="flex items-start justify-between gap-3 pb-3 border-b last:border-0">
                        <div><Badge variant="outline" className="text-xs rounded-sm capitalize mb-1">{(a.activity_type || '').replace(/_/g, ' ')}</Badge><p className="text-sm">{a.content}</p></div>
                        <div className="text-right text-xs text-muted-foreground whitespace-nowrap"><p>{formatDateTime(a.created_at)}</p><p>{a.created_by_name}</p></div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Edit Engagement</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2"><Label className="text-sm font-medium">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="edit-engagement-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-sm font-medium">Type</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label className="text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-sm font-medium">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label className="text-sm font-medium">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></div>
            </div>
            <div className="space-y-2"><Label className="text-sm font-medium">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
            <DialogFooter><Button type="submit" disabled={saving} className="rounded-sm" data-testid="save-engagement-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add person choice */}
      <Dialog open={addChoiceOpen} onOpenChange={setAddChoiceOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Add Person to {eng.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <button onClick={() => { setAddChoiceOpen(false); navigate(`/contacts?create=true`); }} className="flex items-center gap-3 p-4 rounded-sm border hover:border-primary/50 hover:bg-muted/40 text-left" data-testid="eng-create-new-contact">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center"><UserPlus className="h-5 w-5 text-primary" /></div>
              <div><p className="font-medium">Create New Contact</p><p className="text-xs text-muted-foreground">Create a contact, then add them here</p></div>
            </button>
            <button onClick={() => { setAddChoiceOpen(false); setContactQuery(''); setContactResults([]); setAddExistingOpen(true); }} className="flex items-center gap-3 p-4 rounded-sm border hover:border-primary/50 hover:bg-muted/40 text-left" data-testid="eng-add-existing-contact">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
              <div><p className="font-medium">Add Existing Contact</p><p className="text-xs text-muted-foreground">Search and link an existing contact</p></div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add existing person */}
      <Dialog open={addExistingOpen} onOpenChange={setAddExistingOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Add Existing Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input autoFocus placeholder="Search contacts..." value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} className="pl-9 rounded-sm" data-testid="eng-person-search" /></div>
            <div className="max-h-[320px] overflow-y-auto space-y-1" data-testid="eng-person-results">
              {contactResults.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No contacts found</p> : contactResults.map((c) => (
                <button key={c.id} onClick={() => addParticipant(c.id)} className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-muted/50 text-left" data-testid={`eng-person-option-${c.id}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><span className="text-xs font-medium text-primary">{(c.first_name || ' ')[0]}{(c.last_name || ' ')[0]}</span></div>
                  <div className="min-w-0"><p className="font-medium truncate">{c.first_name} {c.last_name}</p><p className="text-xs text-muted-foreground truncate">{c.email}{c.account_name ? ` • ${c.account_name}` : ''}</p></div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link opportunity */}
      <Dialog open={linkOppOpen} onOpenChange={setLinkOppOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Link Existing Opportunity</DialogTitle></DialogHeader>
          <div className="max-h-[360px] overflow-y-auto space-y-1" data-testid="eng-opp-results">
            {oppResults.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No opportunities available</p> : oppResults.map((o) => (
              <button key={o.id} onClick={() => linkOpp(o.id)} className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted/50 text-left" data-testid={`eng-opp-option-${o.id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{o.company_name}</p><p className="text-xs text-muted-foreground">{STAGE_DISPLAY_NAMES[o.stage] || o.stage}</p></div>
                <span className="text-sm">{formatCurrency(o.deal_value)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
